import { apiUrl } from "../api/endpoints";
import React, { Fragment, useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CloudUpload from '@mui/icons-material/CloudUploadOutlined';
import { STATIC_ICONS } from '../Constants';
import Spinner from '../Components/Spinner';
import Select from 'react-select';
import { countries } from '../lib/countries';
import { useAuth } from '../hooks/AuthProvider';
import axios from 'axios';
import './edit.css';
import '../Components/modal.css';
import { DeleteIcon } from '../Icons';

import { LanguageContext } from '../context/LanguageContext';
import orgTranslations from '../languages/organization.json';

import { getErrorMessage } from '../utils/errorUtils';
import Alert from '../Components/Alert';
import { EditOrganizationMainIcon } from '../svg/OrganizationIcons';
import { DetailsMainIcon } from '../svg/UtilsIcons';
import { normalizeLocationListRow } from '../utils/listRowIds';

const defaultCountry =
  countries.find((c) => c.name === 'United States') || { name: 'United States', code: '+1', position: '' };

const getCountryFromPhoneInput = (phoneValue, preferredCountryName) => {
  const normalizedPhone = String(phoneValue || '').replace(/[^\d+]/g, '');

  if (!normalizedPhone.startsWith('+')) {
    return null;
  }

  const matches = countries.filter((country) => normalizedPhone.startsWith(country.code));
  if (matches.length === 0) {
    return null;
  }

  const sortedMatches = [...matches].sort((a, b) => b.code.length - a.code.length);
  const preferredMatch = sortedMatches.find((country) => country.name === preferredCountryName);
  if (preferredMatch) {
    return preferredMatch;
  }

  const defaultMatch = sortedMatches.find((country) => country.name === defaultCountry.name);
  return defaultMatch || sortedMatches[0];
};

const getCountryFlagStyle = (country) => {
  if (country?.flagImage) {
    return {
      backgroundImage: `url(${country.flagImage})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    };
  }

  return {
    backgroundPosition: country?.position || '0px 0px',
  };
};

export const EditOrganization = ({ state, setState, onSuccess, initialData = null }) => {
  const { lang } = useContext(LanguageContext);
  const t = orgTranslations[lang].EditOrganization;
  const isRtl = ['ar', 'ur'].includes(lang);

  const { id: paramId } = useParams();
  const navigate = useNavigate();

  const id = state && state.id ? state.id : paramId;
  const hasInitialData = Boolean(initialData) && Number(initialData?.id) === Number(id);
  const closeModal = () => {
    if (typeof setState === 'function') {
      setState(false);
    }
  };
  
  const timezones = Intl.supportedValuesOf('timeZone');

  const [details, setDetails] = useState(() => (hasInitialData ? initialData : {}));
  const [initialDetails, setInitialDetails] = useState(() => (hasInitialData ? initialData : null));
  const [loading, setLoading] = useState(() => Boolean(id) && !hasInitialData);
  const [submitting, setSubmitting] = useState(false);
  const [previousIcon, setPreviousIcon] = useState('');
  const { permissions } = useAuth();
  const canEditOrganization = permissions['edit_organizations'];
  const canViewLocations = permissions['view_locations'];
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const currentCountry =
    getCountryFromPhoneInput(details?.phone, details?.country) ||
    countries.find((country) => country.name === details?.country) ||
    defaultCountry;

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? 'left' : 'right'
    });
  }, [isRtl]);

  useEffect(() => {
    if (!canEditOrganization || !canViewLocations) return;
    let mounted = true;
    setLocationsLoading(true);
    axios.get(apiUrl('location/options'))
      .then((res) => {
        if (!mounted) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data.map(normalizeLocationListRow) : [];
        setLocations(rows);
      })
      .catch(() => { if (mounted) setLocations([]); })
      .finally(() => { if (mounted) setLocationsLoading(false); });
    return () => { mounted = false; };
  }, [canEditOrganization, canViewLocations]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    if (hasInitialData) {
      setDetails(prev => (prev?.id === initialData.id ? prev : initialData));
      setInitialDetails(prev => (prev?.id === initialData.id ? prev : initialData));
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchOrganizationDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(apiUrl(`organization/get/${id}`));

        if (isCancelled) {
          return;
        }

        if (
          res.data &&
          typeof res.data.data === 'object' &&
          res.data.data !== null
        ) {
          setDetails(res.data.data);
          setInitialDetails(res.data.data);
        } else {
          throw new Error(
            'Received an invalid data format from the server.'
          );
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }

        const errorMessage = getErrorMessage(err);
        showAlert('error', errorMessage);
        setDetails(null);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchOrganizationDetails();

    return () => {
      isCancelled = true;
    };
  }, [hasInitialData, id, initialData, showAlert]); 

  const handleChange = e => {
    const saveBtn = document.querySelector('.edit-btn-save');
    if (saveBtn) {
      saveBtn.classList.remove('disabled');
    }

    setDetails({
      ...details,
      [e.target.name]: e.target.value || ''
    });
  };

  const selectCountry = () => {
    const arrow = document.querySelector('.edit-arrow');
    const menu = document.querySelector('.edit-country-list');

    if (!arrow || !menu) return;

    arrow.classList.toggle('selected');
    menu.classList.toggle('selected');
  };

  const selectCountryItem = e => {
    let mainFlag = document.getElementById('flag'),
      listItem,
      code,
      flag,
      name;

    switch (e.target.className) {
      case 'edit-country-flag':
        flag = e.target;
        listItem = e.target.closest('.edit-country-list-item');
        name = listItem.querySelector('.edit-country-name');
        code = listItem.querySelector('.edit-country-code');
        break;
      case 'edit-country-code':
        code = e.target;
        listItem = e.target.closest('.edit-country-list-item');
        name = listItem.querySelector('.edit-country-name');
        flag = listItem.querySelector('.edit-country-flag');
        break;
      case 'edit-country-name':
        name = e.target;
        listItem = e.target.closest('.edit-country-list-item');
        flag = listItem.querySelector('.edit-country-flag');
        code = listItem.querySelector('.edit-country-code');
        break;
      default:
        listItem = e.target;
        flag = listItem.querySelector('.edit-country-flag');
        code = listItem.querySelector('.edit-country-code');
        name = listItem.querySelector('.edit-country-name');
        break;
    }

    if (mainFlag && flag) {
      mainFlag.style.backgroundPosition = flag.style.backgroundPosition;
      mainFlag.style.backgroundImage = flag.style.backgroundImage || '';
      mainFlag.style.backgroundSize = flag.style.backgroundSize || '';
    }

    setDetails({
      ...details,
      country: name.innerText,
      phone: code.innerText
    });

    const saveBtn = document.querySelector('.edit-btn-save');
    if (saveBtn) {
      saveBtn.classList.remove('disabled');
    }
  };

  const handleTimezoneSelect = selectedOption => {
    const newTimezone = selectedOption ? selectedOption.value : null;
    if (newTimezone !== details.timezone) {
      const saveBtn = document.querySelector('.edit-btn-save');
      if (saveBtn) {
        saveBtn.classList.remove('disabled');
      }
      setDetails({ ...details, timezone: newTimezone });
    }
  };

  const updateOrganization = async e => {
    if (submitting) return;

    if (!canEditOrganization) {
      showAlert(
        'error',
        'You do not have permission to perform this action.'
      );
      return;
    }
    if (e.target.classList.contains('disabled')) {
      return;
    }

    setSubmitting(true);
    try {
      const organizationPayload = {
        name: details.name || '',
        description: details.description || '',
        country: details.country || null,
        phone: details.phone || '',
        timezone: details.timezone || null,
        icon: details.icon || '',
        location_id: details.location_id || null
      };

      const res = await axios.post(
        apiUrl(`organization/update/${id}`),
        organizationPayload,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (previousIcon && previousIcon.length > 0) {
        try {
          const body = ['/icons/' + previousIcon];
          await axios.post(apiUrl(`file/delete`), body);
          console.log(
            'Successfully cleaned up previous icon:',
            previousIcon
          );
        } catch (deleteErr) {
          console.warn(
            'Non-critical error: Failed to delete previous icon.',
            deleteErr
          );
        }
      }

      showAlert(
        'success',
        res.data.message || 'Organization updated successfully.'
      );

      const updatedOrganizationFromServer = res.data.data;
      setDetails(updatedOrganizationFromServer);
      setInitialDetails(updatedOrganizationFromServer);
      setPreviousIcon('');
      if (onSuccess) {
        onSuccess(updatedOrganizationFromServer);
      }

      if (setState) {
        closeModal();
      } else { 
        navigate('/administration/organizations/details/' + id);
      }

    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    const saveBtn = document.querySelector('.edit-btn-save');
    const newIconWasUploaded =
      details.icon &&
      details.icon.length > 0 &&
      saveBtn &&
      !saveBtn.classList.contains('disabled');

    if (newIconWasUploaded) {
      try {
        const body = ['/icons/' + details.icon];
        await axios.post(apiUrl(`file/delete`), body);
        console.log(
          'Orphaned icon cleanup successful:',
          details.icon
        );
      } catch (err) {
        console.warn(
          'Non-critical error: Failed to clean up orphaned icon.',
          err
        );
      }
    }

    if (setState) {
      closeModal();
    } else { 
      navigate(`/administration/organizations/details/${id}`);
    }
  };

  const saveFile = async e => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];

    const prefix = 'org-' + id,
      nameParts = file.name.split('.'),
      suffix = nameParts[nameParts.length - 1],
      random = Date.now().toString(),
      fileName = `${prefix}-${random}.${suffix}`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    formData.append('type', 'icons');

    try {
      const res = await axios.post(
        apiUrl(`file/upload`),
        formData
      );

      showAlert(
        'success',
        res.data.message ||
          'Icon uploaded successfully. Click Apply to save changes.'
      );

      setPreviousIcon(
        details.icon && details.icon.length > 0
          ? details.icon
          : previousIcon
      );

      setDetails({ ...details, icon: fileName });

      const saveBtn = document.querySelector('.edit-btn-save');
      if (saveBtn) {
        saveBtn.classList.remove('disabled');
      }
    } catch (err) {
      console.error('File upload failed:', err);
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);

      e.target.value = null;
    }
  };

  const isDirty =
    initialDetails &&
    JSON.stringify(details) !== JSON.stringify(initialDetails);

  if (loading) {
    if (setState) {
      return (
        <div
          id="modal"
          onClick={setState ? closeModal : undefined}
          style={{
            direction: isRtl ? 'rtl' : 'ltr'
          }}
        >
          <div
            id="popup"
            className="dashboard-modal edit-organization-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
              fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))'
            }}
          >
            <div className="dashboard-header">
              <div className="dashboard-header-icon">
                <DetailsMainIcon />
              </div>
              <div className="dashboard-header-text">
                <h2>{t.title}</h2>
                {t.subtitle && <p>{t.subtitle}</p>}
              </div>
            </div>

            <div className="modal-divider" />

            <div
              className="modal-content dashboard-content"
              style={{
                minHeight: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Spinner />
            </div>
          </div>
        </div>
      );
    }

    return <Spinner />;
  }

  const closeAction = setState ? closeModal : () => navigate(`/administration/organizations/details/${id}`);

  if (!canEditOrganization) {
    return (
      <div
        id="modal"
        onClick={setState ? closeModal : undefined} 
        style={{
          direction: isRtl ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal edit-organization-modal"
          onClick={(e) => e.stopPropagation()} 
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <EditOrganizationMainIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.title}</h2>
              <p>{t.noPermission}</p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content" />

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={closeAction}
            >
              {t.buttonCancel}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  const timezoneOptions = timezones.map(zone => ({
    value: zone,
    label: zone
  }));
  const selectedTimezoneOption = timezoneOptions.find(
    option => option.value === details.timezone
  );

  const timezoneSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'rgba(8, 24, 45, 0.95)',
      borderColor: state.isFocused
        ? '#00C0C7'
        : 'rgba(255, 255, 255, 0.12)',
      boxShadow: state.isFocused
        ? '0 0 0 1px rgba(0, 192, 199, 0.6)'
        : 'none',
      '&:hover': {
        borderColor: '#00C0C7'
      },
      color: '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))',
      minHeight: '44px',
      borderRadius: 8,
      cursor: 'pointer'
    }),
    singleValue: provided => ({
      ...provided,
      color: '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))'
    }),
    input: provided => ({
      ...provided,
      color: '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))'
    }),
    placeholder: provided => ({
      ...provided,
      color: 'rgba(200, 220, 255, 0.4)',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))'
    }),
    menu: provided => ({
      ...provided,
      backgroundColor: '#071128',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: 8,
      overflow: 'hidden',
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#00C0C7'
        : state.isFocused
        ? 'rgba(13, 42, 77, 0.9)'
        : '#071128',
      color: state.isSelected ? '#021020' : '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))',
      '&:active': {
        backgroundColor: '#00A0AF'
      },
      cursor: 'pointer'
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: '#FFFFFF',
      paddingRight: 12,
      '&:hover': {
        color: '#00C0C7'
      }
    }),
    clearIndicator: provided => ({
      ...provided,
      color: '#FFFFFF',
      '&:hover': {
        color: '#00C0C7'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  if (!details) {
    return (
      <div
        id="modal"
        onClick={setState ? closeModal : undefined} 
        style={{
          direction: isRtl ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal edit-organization-modal"
          onClick={(e) => e.stopPropagation()}
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <EditOrganizationMainIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.title}</h2>
              <p>{orgTranslations[lang].noData}</p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content" />

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={closeAction}
            >
              {t.buttonCancel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="modal"
      onClick={setState ? closeModal : undefined} 
      style={{
        direction: isRtl ? 'rtl' : 'ltr'
      }}
    >
      <Alert
        open={alertState.open}
        close={() =>
          setAlertState({ ...alertState, open: false })
        }
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        id="popup"
        className="dashboard-modal edit-organization-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
          fontSize:
            'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }}
      >
        <div className="dashboard-header">
          <div className="dashboard-header-icon">
            <DetailsMainIcon />
          </div>
          <div className="dashboard-header-text">
            <h2>{t.title}</h2>
            {t.subtitle && <p>{t.subtitle}</p>}
          </div>
        </div>

        <div className="modal-divider" />

        <div className="modal-content dashboard-content edit-layout-grid">
          <div className="form-section">
            <div className="form-section-title">
              <span className="form-section-title-text">
                {t.Logo}
              </span>
            </div>

            <div className="form-item">
              <div className="edit-form-item edit-logo-wrapper">
                {details.icon && details.icon.length > 0 ? (
                  <Fragment>
                    <input
                      type="file"
                      accept="image/*"
                      className="edit-logo-section"
                      title=""
                      onChange={saveFile}
                    />
                    <img
                      src={STATIC_ICONS + details.icon}
                      alt=""
                      onClick={() => {
                        const saveBtn =
                          document.querySelector('.edit-btn-save');
                        if (saveBtn) {
                          saveBtn.classList.remove('disabled');
                        }
                        setPreviousIcon(details.icon);
                        setDetails({
                          ...details,
                          icon: ''
                        });
                      }}
                    />
                    <DeleteIcon />
                  </Fragment>
                ) : (
<Fragment>
  <input
    type="file"
    accept="image/*"
    className="edit-logo-section"
    title=""
    onChange={saveFile}
  />

  <div
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      textAlign: 'center',
    }}
  >
    <CloudUpload
      style={{
        color: 'var(--accent)',
        transform: 'scale(2.5)',
        marginBottom: '14px'
      }}
    />

    <div
      style={{
        fontWeight: 500,
        marginBottom: '8px'
      }}
    >
      {t.uploadLogo}
    </div>

    <div
      className="edit-helper"
      style={{
        maxWidth: '90%',
        opacity: 0.85
      }}
    >
      {t.uploadHelper}
    </div>
  </div>
</Fragment>

                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-title-text">
                  {t.labelName}
                </span>
              </div>

              <div className="form-item">
                <div className="field-with-counter">
                  <input
                    type="text"
                    name="name"
                    className="add-column"
                    placeholder={t.placeholderName}
                    maxLength={50}
                    value={details.name || ''}
                    onChange={handleChange}
                  />
                  <span className="field-counter">
                    {details.name?.length || 0} / 50
                  </span>
                </div>
                <span className="edit-helper edit-helper-name">
                  {t.helperName}
                </span>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-title-text">
                  {t.labelDesc}
                </span>
              </div>

              <div className="form-item">
                <div className="field-with-counter field-with-counter--textarea">
                  <textarea
                    style={{ resize: 'none' }}
                    name="description"
                    rows={5}
                    maxLength={500}
                    className="add-column textarea-large"
                    placeholder={t.placeholderDesc}
                    value={details.description || ''}
                    onChange={handleChange}
                  />
                  <span className="field-counter">
                    {details.description?.length || 0} / 500
                  </span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-title-text">
                  {t.labelPhone}
                </span>
              </div>

              <div className="form-item">
                <div style={{ position: 'relative' }}>
                  <input
                    style={{ paddingInlineStart: '56px' }}
                    type="text"
                    name="phone"
                    className="add-column"
                    placeholder="+1(222)333-44-55"
                    value={details.phone || ''}
                    onChange={handleChange}
                  />
                  <div
                    className="edit-select-country"
                    title="Select Country"
                    onClick={selectCountry}
                  >
                    <div id="flag" style={getCountryFlagStyle(currentCountry)} />
                    <span className="edit-arrow" />
                    <ul className="edit-country-list">
                      {countries.map(country => (
                        <li
                          className="edit-country-list-item"
                          key={countries.indexOf(country)}
                          onClick={selectCountryItem}
                        >
                          <span style={getCountryFlagStyle(country)} className="edit-country-flag" />
                          <span className="edit-country-name">
                            {country.name}
                          </span>
                          <span className="edit-country-code">
                            {country.code}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <span className="edit-helper">
                  {t.helperPhone
                    .split('\n')
                    .map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </span>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-title-text">
                  {t.labelTimezone}
                </span>
              </div>

              <div className="form-item">
                <Select
                  id="timezone"
                  options={timezoneOptions}
                  value={selectedTimezoneOption}
                  onChange={handleTimezoneSelect}
                  classNamePrefix="react-select"
                  styles={timezoneSelectStyles}
                  placeholder={t.labelTimezone}
                  isClearable
                />
              </div>
            </div>

            {canViewLocations ? (
              <div className="form-section">
                <div className="form-section-title">
                  <span className="form-section-title-text">
                    {t.labelLocation || 'Location'}
                  </span>
                </div>
                <div className="form-item">
                  <Select
                    id="organization-location"
                    isClearable
                    isSearchable
                    isLoading={locationsLoading}
                    classNamePrefix="react-select"
                    styles={timezoneSelectStyles}
                    placeholder={t.placeholderLocation || 'Select location (optional)'}
                    options={locations.map((location) => ({
                      value: location.id,
                      label: location.address ? `${location.name} — ${location.address}` : (location.name || `#${location.id}`),
                    }))}
                    value={locations.map((location) => ({
                      value: location.id,
                      label: location.address ? `${location.name} — ${location.address}` : (location.name || `#${location.id}`),
                    })).find((option) => Number(option.value) === Number(details.location_id)) || null}
                    onChange={(option) => {
                      const saveBtn = document.querySelector('.edit-btn-save');
                      if (saveBtn) saveBtn.classList.remove('disabled');
                      setDetails((prev) => ({ ...prev, location_id: option ? option.value : null }));
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="modal-footer dashboard-footer">
          <button
            className="btn btn-cancel"
            onClick={cancel}
            disabled={submitting}
          >
            {t.buttonCancel}
          </button>
          <button
            className={
              'btn btn-apply dashboard-primary-btn edit-btn-save' +
              (isDirty ? '' : ' disabled')
            }
            onClick={updateOrganization}
            disabled={submitting || !isDirty}
            style={{marginLeft:'70%'}}
          >
            {t.buttonSave}
          </button>
        </div>
      </div>
    </div>
  );
};
